<template>
  <div>
    <BaseProofOrOrderItemListItem
      analytics-name="Order item"
      :salesforce-id="orderItem.salesforceId"
      :short-title="orderItem.item.shortTitle"
      :item-code="orderItem.item.code"
      :overall-status="orderItemData.overallStatus"
      :proof-id="orderItem.proofId"
      :is-add-on="orderItemData.isAddOn"
      :add-ons="proofAddOns"
      :proof="orderItemData.proof"
      :base-proof="baseProof"
      :delivery-type="orderItem.deliveryType"
      :days-late="orderItemData.daysLate"
      :days-delayed-by-approvals="orderItemData.daysDelayedByApprovals"
      :shipments="orderItemData.shipments"
    >
      {{ orderItem.quantity }}
      <template
        v-if="
          (orderItemData.overallStatus === 'Shipped' ||
            orderItemData.overallStatus === 'Delivered') &&
          orderItem.deliveryType === 'Mail & Message'
        "
      >
        Mailed
      </template>
      <a
        v-else-if="
          (orderItemData.overallStatus === 'Shipped' ||
            orderItemData.overallStatus === 'Delivered') &&
          orderItemData.shipments.length &&
          orderItemData.shipments[0].trackingUrl
        "
        :href="orderItemData.shipments[0].trackingUrl"
        target="_blank"
        rel="noopener"
        :class="
          orderItemData.isAddOn
            ? 'text-decoration-none text--darken-1 grey--text'
            : 'text--primary'
        "
        v-text="orderItemData.overallStatus"
      />
      <template v-else>
        {{ orderItemData.overallStatus }}
      </template>
      <template
        v-if="
          orderItemData.overallStatus.startsWith('Pending') ||
          orderItemData.overallStatus === 'At Customer'
        "
      >
        <template v-if="orderItemData.proofLastModifiedDate">
          • Updated:
          {{
            formatDateDistanceToNowIfRecent(orderItemData.proofLastModifiedDate)
          }}
        </template>
      </template>
      <!-- Ensure there's a first ship date to exclude items without shipments, like custom charges -->
      <template
        v-else-if="
          orderItemData.overallStatus === 'Shipped' &&
          orderItemData.firstShipDate
        "
      >
        <Tooltip top analytics-name="Ship date">
          <template #default="{ attrs, on }">
            <span v-bind="attrs" v-on="on">
              {{ formatDateDistanceToNowIfRecent(orderItemData.firstShipDate) }}
            </span>
          </template>
          <template #tooltip>
            First Shipped:
            {{ formatDateDistanceToNowIfRecent(orderItemData.firstShipDate) }}
            <br />
            <template
              v-if="
                orderItemData.promisedShipDate.getTime() !==
                orderItemData.adjustedPromisedShipDate.getTime()
              "
            >
              Promised (adjusted for edits):
              {{
                formatDateDistanceToNowIfRecent(
                  orderItemData.adjustedPromisedShipDate
                )
              }}
              <br />
            </template>
            Promised:
            {{
              formatDateDistanceToNowIfRecent(orderItemData.promisedShipDate)
            }}
          </template>
        </Tooltip>
      </template>
    </BaseProofOrOrderItemListItem>
    <!-- Shipment tracking details for shipped/delivered items (latest first) -->
    <ShipmentTrackingDetails
      v-for="shipment in shippedShipments"
      :key="shipment.trackingNumber"
      :shipment="shipment"
    />
  </div>
</template>

<script lang="ts">
import { identity } from 'lodash-es'
import { computed, defineComponent, PropType } from 'vue'
import { getModule } from 'vuex-module-decorators'

import BaseProofOrOrderItemListItem from '@/components/BaseProofOrOrderItemListItem.vue'
import { formatDateDistanceToNowIfRecent } from '@/components/mixins/dateFormatters'
import { OrderItemData } from '@/components/mixins/orderUtils'
import ShipmentTrackingDetails from '@/components/ShipmentTrackingDetails.vue'
import Tooltip from '@/components/Tooltip.vue'
import { isShipmentTooOld } from '@/services/shipmentTracking'
import Salesforce from '@/store/modules/salesforce'
import { OrderItem } from '@/store/modules/types/order'

export default defineComponent({
  components: {
    BaseProofOrOrderItemListItem,
    ShipmentTrackingDetails,
    Tooltip
  },
  props: {
    orderItem: {
      type: Object as PropType<OrderItem>,
      required: true
    },
    // TODO: Restructure and remove this once Vuex stores are normalized with vuex-orm
    orderItemData: {
      type: Object as PropType<OrderItemData>,
      required: true
    }
  },
  setup(props) {
    const salesforce = getModule(Salesforce)
    const baseProofSalesforceId = props.orderItemData.proof?.baseProof?.id
    const baseProof = baseProofSalesforceId
      ? salesforce.proofBySalesforceId(baseProofSalesforceId)
      : null

    const proofAddOns = props.orderItemData.addOns
      .map((addOn) => salesforce.proofByProofId(addOn.proof?.id as string))
      // Proof should always exist if we were able to know something was an add-on,
      // since a project and proof would be necessary to do so, so this is more of an extra precaution.
      .filter(identity)

    const isShipped =
      props.orderItemData.overallStatus === 'Shipped' ||
      props.orderItemData.overallStatus === 'Delivered'

    // Shipments with tracking numbers that aren't too old, latest first
    const shippedShipments = computed(() => {
      if (!isShipped) return []
      return [...props.orderItemData.shipments]
        .filter((s) => s.trackingNumber && !isShipmentTooOld(s.shipTime))
        .reverse()
    })

    return {
      baseProof,
      proofAddOns,
      shippedShipments,
      formatDateDistanceToNowIfRecent
    }
  }
})
</script>
